import { useState } from 'react';
import type { Key, Selection } from 'react-aria-components';
import {
  Button,
  Group,
  IconButton,
  Input,
  Menu,
  MenuItem,
  MenuTrigger,
  Popover,
  SearchField,
} from '@launchpad-ui/components';
import { Icon } from '@launchpad-ui/icons';
import { clsx } from 'clsx';
import { debounce } from 'lodash';

import styles from './MultiSelectWithSearch.module.css';

type MultiSelectWithSearchProps = {
  options: MultiSelectOption[];
  ariaLabel: string;
  values?: string[];
  placeholder?: string;
  onSelect?: (selection: string[]) => void;
  searchBreakpoint?: number;
  maxDisplayedOptions?: number;
  disallowEmptySelection?: boolean;
  isDisabled?: boolean;
  shortenLabels?: boolean;
  /** Renders the trigger in the error state (red border). */
  isInvalid?: boolean;
  /**
   * Label to show on the trigger when `isInvalid` is set, e.g. the name of a
   * selected value that no longer exists in `options`. Shown instead of the
   * placeholder so the user can see which value needs replacing, without that
   * value appearing in the options list.
   */
  invalidValueLabel?: string;
};

export interface MultiSelectOption {
  value: string;
  label?: string;
}

export function MultiSelectWithSearch({
  options,
  ariaLabel,
  values,
  placeholder,
  onSelect,
  searchBreakpoint = 1,
  maxDisplayedOptions = 2,
  disallowEmptySelection = false,
  isDisabled = false,
  shortenLabels = false,
  isInvalid = false,
  invalidValueLabel,
}: MultiSelectWithSearchProps) {
  const selectedKeys = values ? new Set<Key>(values) : new Set<Key>();
  const [displayedOptions, setDisplayedOptions] = useState<MultiSelectOption[]>(options);

  const handleSelect = (selected: Selection) => {
    if (selected === 'all') {
      return;
    }
    const element = Array.from(selected).map((key: Key) => key.toString());
    onSelect && onSelect(element);
  };

  function handleSearch(newSearchValue: string) {
    setDisplayedOptions(options.filter((o) => o.label?.toLowerCase().includes(newSearchValue.toLowerCase())));
  }

  const debounced = debounce(handleSearch, 300);

  const optionsComparator = (a: MultiSelectOption, b: MultiSelectOption) => {
    if (selectedKeys.has(a.value) && !selectedKeys.has(b.value)) {
      return -1;
    } else if (!selectedKeys.has(a.value) && selectedKeys.has(b.value)) {
      return 1;
    } else {
      return 0;
    }
  };

  const getSelectedLabelsString = () => {
    // When the current value is invalid (e.g. it no longer exists in `options`),
    // surface its name on the trigger so the user knows what to replace.
    if (isInvalid && invalidValueLabel) {
      return invalidValueLabel;
    }

    const selectedLabels = options
      .filter((o) => Array.from(selectedKeys).slice(0, maxDisplayedOptions).includes(o.value))
      .map((o) => o.label);

    const joined = selectedLabels.join(', ');

    if (shortenLabels) {
      const truncateAtLength = 12;
      if (joined.length > 20) {
        const shortenedLabels = selectedLabels.map((label) =>
          label && label.length > truncateAtLength ? `${label?.substring(0, truncateAtLength).trim()}...` : label,
        );
        return shortenedLabels.join(', ');
      }
    }
    return joined || placeholder;
  };

  return (
    <MenuTrigger onOpenChange={(isOpen) => isOpen && setDisplayedOptions([...options].toSorted(optionsComparator))}>
      <Button
        className={clsx(styles.dropdownButton, isInvalid && styles.invalid)}
        isDisabled={isDisabled}
        data-invalid={isInvalid || undefined}
      >
        <span className={styles.selectedOptions}>{getSelectedLabelsString()}</span>
        {Array.from(selectedKeys).length > maxDisplayedOptions && (
          <span className={styles.moreOptions}>{`+${Array.from(selectedKeys).length - maxDisplayedOptions} more`}</span>
        )}
        <Icon name="chevron-down" size="small" />
      </Button>
      <Popover placement="bottom left">
        {options.length > searchBreakpoint && (
          <div className={styles.searchFieldContainer}>
            <SearchField onChange={debounced} aria-label={`${placeholder} MultiSelect Search`}>
              <Group>
                <Icon name="search" size="small" />
                <Input placeholder={placeholder} />
                <IconButton aria-label="clear" icon="cancel-circle-outline" size="small" variant="minimal" />
              </Group>
            </SearchField>
          </div>
        )}
        <Menu
          onSelectionChange={handleSelect}
          selectedKeys={selectedKeys}
          selectionMode="multiple"
          className={styles.menu}
          aria-label={ariaLabel}
          disallowEmptySelection={disallowEmptySelection}
          renderEmptyState={() => <div className={styles.menuEmptyState}>No options found</div>}
        >
          {displayedOptions.map((o, index) => (
            <MenuItem
              id={o.value}
              key={`MenuItem-${o?.label}-${index}`}
              className={styles.menuItem}
              aria-label={o.label}
            >
              {o?.label}
            </MenuItem>
          ))}
        </Menu>
      </Popover>
    </MenuTrigger>
  );
}
